import { useCallback, useState, useRef, useEffect } from 'react';
import { newId } from '../dataFile';

function todayStr() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function emptyRow() {
  return { id: newId(), number: '', sr_no: '', brand: '', description: '', quantity: '1' };
}

const ROW_FIELDS = ['number', 'sr_no', 'brand', 'description', 'quantity'];
const ROW_HEADERS = ['L Number', 'Sr No', 'Brand', 'Description', 'Qty'];

export default function ChallanForm({
  form, setForm, clients, company, onUpdateCompany, onAddClient, onDeleteClient,
  onGenerate, onDownloadPreview, onDuplicate, hasPrevious, readOnly = false,
}) {
  const formRef = useRef(null);

  // Ctrl+Enter / Cmd+Enter anywhere in the form -> generate
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        onGenerate();
      }
    }
    const el = formRef.current;
    el?.addEventListener('keydown', onKey);
    return () => el?.removeEventListener('keydown', onKey);
  }, [onGenerate]);

  const setField = useCallback((path, value) => {
    setForm((f) => {
      const next = { ...f };
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = Array.isArray(cur[parts[i]]) ? [...cur[parts[i]]] : { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }, [setForm]);

  const updateRow = useCallback((idx, field, value) => {
    setForm((f) => {
      const rows = f.rows.slice();
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...f, rows };
    });
  }, [setForm]);

  const addRow = useCallback(() => {
    setForm((f) => ({ ...f, rows: [...f.rows, emptyRow()] }));
  }, [setForm]);

  const removeRow = useCallback((idx) => {
    setForm((f) => {
      if (f.rows.length === 1) return f;
      const rows = f.rows.slice();
      rows.splice(idx, 1);
      return { ...f, rows };
    });
  }, [setForm]);

  const onRowKey = useCallback((e, rowIdx, fieldIdx) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      // Enter on last cell of last row -> add row & focus first cell
      const isLastField = fieldIdx === ROW_FIELDS.length - 1;
      const isLastRow = rowIdx === form.rows.length - 1;
      if (isLastField && isLastRow) {
        e.preventDefault();
        addRow();
        // focus the first input of the new row after render
        setTimeout(() => {
          const nextEl = formRef.current?.querySelector(
            `[data-row="${rowIdx + 1}"][data-field="0"]`
          );
          nextEl?.focus();
        }, 0);
      }
    }
  }, [form.rows.length, addRow]);

  // Bulk paste: TSV (Excel/Sheets) or CSV. If multi-row content is pasted into
  // any row cell, fill rows starting at the focused row.
  const onRowPaste = useCallback((e, rowIdx, fieldIdx) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text || (!text.includes('\n') && !text.includes('\t'))) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n').filter(Boolean);
    const parsed = lines.map((ln) => {
      const cells = ln.includes('\t') ? ln.split('\t') : ln.split(',');
      return cells.map((c) => c.trim());
    });
    setForm((f) => {
      const rows = f.rows.slice();
      parsed.forEach((cells, i) => {
        const target = rowIdx + i;
        // ensure row exists
        while (rows.length <= target) rows.push(emptyRow());
        const row = { ...rows[target] };
        cells.forEach((val, j) => {
          const fName = ROW_FIELDS[fieldIdx + j];
          if (fName) row[fName] = val;
        });
        rows[target] = row;
      });
      return { ...f, rows };
    });
  }, [setForm]);

  const selectedClient = clients.find((c) => c.id === form.clientId);
  const isCustom = !form.clientId;

  return (
    <fieldset ref={formRef} disabled={readOnly} className="space-y-4 [&:disabled]:opacity-90">
      {/* Company info — appears on the PDF header */}
      <CompanySection company={company} onUpdateCompany={onUpdateCompany} />

      {/* Top row */}
      <div className="card bg-base-200">
        <div className="card-body p-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="form-control">
              <span className="label-text">Challan No</span>
              <input type="number" className="input input-bordered input-sm w-full"
                     value={form.challanNo}
                     onChange={(e) => setField('challanNo', Number(e.target.value))} />
            </label>
            <label className="form-control">
              <span className="label-text">Date</span>
              <div className="join">
                <input type="text" className="input input-bordered input-sm join-item w-full"
                       value={form.dateStr}
                       onChange={(e) => setField('dateStr', e.target.value)} />
                <button type="button" className="btn btn-sm join-item"
                        onClick={() => setField('dateStr', todayStr())}>Today</button>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Client */}
      <ClientSection
        clients={clients}
        selectedClient={selectedClient}
        isCustom={isCustom}
        form={form}
        setForm={setForm}
        setField={setField}
        onAddClient={onAddClient}
        onDeleteClient={onDeleteClient}
      />

      {/* Line items */}
      <div className="card bg-base-200">
        <div className="card-body p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold">Line items</h3>
            <span className="text-xs opacity-70">
              Tab between cells · Enter at last cell adds a row · Paste TSV from Excel · *** = same description as previous · Ctrl+Enter generates
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="table table-xs">
              <thead>
                <tr>
                  {ROW_HEADERS.map((h) => <th key={h}>{h}</th>)}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.rows.map((row, ri) => (
                  <tr key={row.id}>
                    {ROW_FIELDS.map((field, fi) => (
                      <td key={field}>
                        <input
                          className="input input-bordered input-xs w-full"
                          data-row={ri}
                          data-field={fi}
                          value={row[field]}
                          onChange={(e) => updateRow(ri, field, e.target.value)}
                          onKeyDown={(e) => onRowKey(e, ri, fi)}
                          onPaste={(e) => onRowPaste(e, ri, fi)}
                        />
                      </td>
                    ))}
                    <td>
                      <button className="btn btn-xs btn-ghost"
                              onClick={() => removeRow(ri)}
                              disabled={form.rows.length === 1}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-2">
            <button className="btn btn-sm" onClick={addRow}>+ Add row</button>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 sticky bottom-0 bg-base-100 py-2">
        <button className="btn btn-sm" onClick={onDuplicate} disabled={!hasPrevious}>
          Duplicate from previous
        </button>
        <div className="flex-1" />
        <button className="btn btn-sm" onClick={onDownloadPreview}>
          Download preview only
        </button>
        <button className="btn btn-sm btn-primary" onClick={onGenerate}>
          Generate & download (Ctrl+Enter)
        </button>
      </div>
    </fieldset>
  );
}

function CompanySection({ company, onUpdateCompany }) {
  const isEmpty = !company.name && !company.address && !company.contact && !company.gstin;
  const [open, setOpen] = useState(isEmpty);

  return (
    <div className="card bg-base-200">
      <div className="card-body p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">
            Company info <span className="text-xs font-normal opacity-60">(appears on PDF header)</span>
          </h3>
          <button type="button" className="btn btn-xs btn-ghost"
                  onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Edit'}
          </button>
        </div>
        {!open && !isEmpty && (
          <div className="text-sm opacity-80 mt-1">
            <b>{company.name}</b> · {company.gstin}
          </div>
        )}
        {!open && isEmpty && (
          <div className="text-sm text-warning mt-1">
            No company info set — header on the PDF will be blank. Click Edit.
          </div>
        )}
        {open && (
          <div className="grid grid-cols-1 gap-2 mt-2">
            <input className="input input-bordered input-sm" placeholder="Company name (e.g. Pinaki Infotech)"
                   value={company.name || ''}
                   onChange={(e) => onUpdateCompany({ name: e.target.value })} />
            <input className="input input-bordered input-sm" placeholder="Address line"
                   value={company.address || ''}
                   onChange={(e) => onUpdateCompany({ address: e.target.value })} />
            <input className="input input-bordered input-sm" placeholder="PAN / email / phone line"
                   value={company.contact || ''}
                   onChange={(e) => onUpdateCompany({ contact: e.target.value })} />
            <input className="input input-bordered input-sm" placeholder="GSTIN line (e.g. GSTIN : 07XXXXXXXXX)"
                   value={company.gstin || ''}
                   onChange={(e) => onUpdateCompany({ gstin: e.target.value })} />
          </div>
        )}
      </div>
    </div>
  );
}

function ClientSection({ clients, selectedClient, isCustom, form, setForm, setField, onAddClient, onDeleteClient }) {
  const [query, setQuery] = useState(selectedClient?.name || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => { setQuery(selectedClient?.name || ''); }, [selectedClient]);

  const filtered = clients.filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    (c.code || '').toLowerCase().includes(query.toLowerCase())
  );

  function pickClient(c) {
    setForm((f) => ({ ...f, clientId: c.id }));
    setShowSuggestions(false);
  }

  function chooseCustom() {
    setForm((f) => ({ ...f, clientId: null }));
    setShowSuggestions(false);
  }

  function saveCurrentAsClient() {
    const name = form.custom.name.trim();
    if (!name) { alert('Enter a name first'); return; }
    onAddClient({
      code: name.split(/\s+/)[0].slice(0, 8),
      name,
      lines: form.custom.lines.filter(Boolean),
      gst: form.custom.gst,
    });
  }

  return (
    <div className="card bg-base-200">
      <div className="card-body p-4">
        <h3 className="font-bold mb-2">Client</h3>

        <div className="relative">
          <input
            className="input input-bordered input-sm w-full"
            placeholder="Type to search clients, or pick Custom below"
            value={isCustom ? '— Custom —' : query}
            onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          />
          {showSuggestions && (
            <ul className="menu menu-sm bg-base-100 rounded-box shadow absolute z-10 w-full mt-1 max-h-60 overflow-auto">
              {filtered.map((c) => (
                <li key={c.id}>
                  <a onClick={() => pickClient(c)}>
                    <b>{c.code || c.name}</b>
                    <span className="opacity-60 ml-2">{c.name}</span>
                  </a>
                </li>
              ))}
              {!filtered.length && <li className="px-3 py-1 opacity-60 text-xs">No matches</li>}
              <li><a onClick={chooseCustom}><i>— Custom (one-time) —</i></a></li>
            </ul>
          )}
        </div>

        {selectedClient && (
          <div className="mt-3 text-sm opacity-80">
            <div><b>{selectedClient.name}</b></div>
            {selectedClient.lines.map((l, i) => <div key={i}>{l}</div>)}
            <div>{selectedClient.gst}</div>
            <div className="mt-2">
              {!confirmDelete ? (
                <button className="btn btn-xs btn-ghost text-error"
                        onClick={() => setConfirmDelete(true)}>Delete client</button>
              ) : (
                <span className="text-xs">
                  Confirm?{' '}
                  <button className="btn btn-xs btn-error"
                          onClick={() => {
                            const id = selectedClient.id;
                            setConfirmDelete(false);
                            onDeleteClient?.(id);
                          }}>Yes, delete</button>{' '}
                  <button className="btn btn-xs btn-ghost"
                          onClick={() => setConfirmDelete(false)}>Cancel</button>
                </span>
              )}
            </div>
          </div>
        )}

        {isCustom && (
          <div className="mt-3 grid grid-cols-1 gap-2">
            <input className="input input-bordered input-sm" placeholder="Customer name"
                   value={form.custom.name}
                   onChange={(e) => setField('custom.name', e.target.value)} />
            {[0,1,2,3].map((i) => (
              <input key={i} className="input input-bordered input-sm"
                     placeholder={`Address line ${i + 1}`}
                     value={form.custom.lines[i] || ''}
                     onChange={(e) => {
                       const lines = form.custom.lines.slice();
                       lines[i] = e.target.value;
                       setField('custom.lines', lines);
                     }} />
            ))}
            <input className="input input-bordered input-sm" placeholder="Customer GST"
                   value={form.custom.gst}
                   onChange={(e) => setField('custom.gst', e.target.value)} />
            <button className="btn btn-sm btn-outline mt-1" onClick={saveCurrentAsClient}>
              + Save as new client
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
